import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Text } from "@mantine/core";
import DataWrapper from "../../../common/components/DataWrapper/DataWrapper";
import PaymentDrawer from "../components/PaymentDrawer/PaymentDrawer";
import PaymentStats from "../components/PaymentStats/PaymentStats";
import PaymentToolbar from "../components/PaymentToolbar/PaymentToolbar";
import usePaymentStats from "../hooks/usePaymentStats";
import {
  PAYMENTS_ENDPOINT,
  PAYMENTS_SWR_CONFIG,
  INITIAL_PAYMENT_FILTERS,
  buildPaymentFilterQuery,
  formatCurrencyAmount,
  withSearchQuery,
  withUserEmailQuery,
  downloadPaymentInvoice,
} from "../utils/paymentConstants";
import { FormatDate } from "../../../helper/DateFormatting";
import { USER_TYPES } from "../../../utils/userTypes";

const dash = (val) => (val == null || val === "" ? "—" : val);

const AmountCell = ({ row }) => {
  if (!row.currency && !row.total_amount) return <Cell value="—" />;

  const currency = row.currency || "";
  const discount = Number(row.discount) || 0;

  return (
    <span style={{ display: "block", maxWidth: "100%", margin: "9px 0" }}>
      <Text
        size="sm"
        fw={500}
        c="#6C94B4"
        style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {`${formatCurrencyAmount(currency, row.total_amount)} ${currency}`}
      </Text>
      {discount > 0 ? (
        <Text size="xs" fw={500} c="red" style={{ display: "block" }}>
          {`-${currency}${discount.toLocaleString("en-IN")} disc`}
        </Text>
      ) : null}
    </span>
  );
};

const Cell = ({ value, highlight }) => (
  <Text
    size="sm"
    my="9px"
    fw={500}
    c={highlight ? "#092C4C" : "#6C94B4"}
    component="span"
    style={{
      display: "block",
      maxWidth: "100%",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {dash(value)}
  </Text>
);

const Payments = () => {
  const roles = useSelector((state) => state.auth?.role || []);
  const userType = useSelector((state) => state.auth?.user_type) || [];
  const isSuperUser = userType.includes(USER_TYPES.SUPERUSER);
  const isOperator =
    userType.includes(USER_TYPES.OPERATOR) || roles.includes("Operator");
  const operatorEmail = useSelector((state) => state.operatorProfile?.operator?.email);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [filters, setFilters] = useState(INITIAL_PAYMENT_FILTERS);
  const [searchQuery, setSearchQuery] = useState("");

  const filterQuery = useMemo(
    () => buildPaymentFilterQuery(filters),
    [filters]
  );

  const additionalFilterQuery = useMemo(
    () => (isOperator ? withUserEmailQuery(filterQuery, operatorEmail) : filterQuery),
    [filterQuery, isOperator, operatorEmail]
  );

  const statsQuery = useMemo(
    () => withSearchQuery(additionalFilterQuery, searchQuery),
    [additionalFilterQuery, searchQuery]
  );

  const listEndpoint =
    isOperator && operatorEmail
      ? `issuance/user-specific-payments/?user_email=${encodeURIComponent(operatorEmail)}`
      : PAYMENTS_ENDPOINT;

  const { stats, loading: statsLoading } = usePaymentStats(statsQuery);

  const updateFilter = useCallback(
    (key, value) => setFilters((prev) => ({ ...prev, [key]: value })),
    []
  );
  const clearFilters = useCallback(() => setFilters(INITIAL_PAYMENT_FILTERS), []);

  const handleDownloadInvoice = useCallback((row) => downloadPaymentInvoice(row), []);

  const handleTripRedirect = (row) => `/issuance/policies/trips/${row.trip}`;

  const handleRowClick = useCallback((row) => {
    setSelectedPayment(row);
    setDrawerOpen(true);
  }, []);

  const config = {
    onRowClick: handleRowClick,
    doubleClickRow: true,
    fields: [
      { key: "transaction_id", label: "Transaction ID", width: 180, render: (r) => <Cell value={r.transaction_id} highlight /> },
      { key: "email", label: "Email", width: 200, render: (r) => <Cell value={r.email} /> },
      { key: "trip", label: "Trip", width: 80, render: (r) => <Cell value={r.trip} /> },
      { key: "payment_type", label: "Payment Type", width: 110, render: (r) => <Cell value={r.payment_type} /> },
      { key: "gateway", label: "Gateway", width: 130, render: (r) => <Cell value={r.gateway} /> },
      { key: "method", label: "Method", width: 100, render: (r) => <Cell value={r.method} /> },
      {
        key: "total_amount",
        label: "Total Amount",
        width: 130,
        render: (r) => <AmountCell row={r} />,
      },
      { key: "promocode", label: "Promocode", width: 100, render: (r) => <Cell value={r.promocode} /> },
      {
        key: "created_on",
        label: "Date",
        width: 190,
        render: (r) => <Cell value={r.created_on ? FormatDate(r.created_on, "datetime") : null} />,
      },
      { key: "invoice_number", label: "Invoice No.", width: 110, render: (r) => <Cell value={r.invoice_number} /> },
      { key: "transaction_status", label: "Status", width: 100, type: "status" },
    ],
    handler: [{ label: "Invoice", handler: (row) => handleDownloadInvoice(row) }],
    actions: [{ label: "Trip", to: (row) => handleTripRedirect(row), isButton: true }],
  };

  const toolbar = PaymentToolbar({
    filters,
    onChange: updateFilter,
    currencies: stats?.all_currencies,
    isFull: !isOperator,
    showClear: Boolean(filterQuery),
    onClear: clearFilters,
  });

  return (
    <>
      <PaymentStats stats={stats} loading={statsLoading} showRevenue={isSuperUser} />
      <DataWrapper
        endpoint={listEndpoint}
        config={config}
        additionalFilterQuery={isOperator ? filterQuery : additionalFilterQuery}
        toolbarBottomContent={toolbar.toolbarBottomContent}
        searchPlaceholder="Search transaction ID, email, customer..."
        onSearchChange={setSearchQuery}
        swrConfig={PAYMENTS_SWR_CONFIG}
      />
      <PaymentDrawer
        opened={drawerOpen}
        payment={selectedPayment}
        onClose={() => setDrawerOpen(false)}
        onDownloadInvoice={handleDownloadInvoice}
      />
    </>
  );
};

export default Payments;
